import { z } from 'zod'

const _localDatetime = z.iso.datetime({ local: true })
// Strapi 는 시각을 UTC ISO(`2026-10-01T00:00:00.000Z`)로 낸다.
const _utcDatetime = z.iso.datetime()

/***************** 학부모앱 공지 (메인 서버) *****************/
export const PARENTS_APP_NOTICE_TYPE = ['NOTICE', 'NEWS', 'PLAN'] as const
export const ParentsAppNoticeTypeSchema = z.enum(PARENTS_APP_NOTICE_TYPE)
export type ParentsAppNoticeType = z.infer<typeof ParentsAppNoticeTypeSchema>

export const ParentsAppNoticeSchema = z.object({
  id: z.number(),
  teacherName: z.string(),
  type: ParentsAppNoticeTypeSchema,
  title: z.string(),
  content: z.string(),
  /** 학부모앱 노출 여부 */
  marked: z.boolean(),
  createDatetime: _localDatetime,
})
export type ParentsAppNotice = z.infer<typeof ParentsAppNoticeSchema>

/***************** Strapi(hcms) 콘텐츠 *****************/
// Strapi 는 Swagger 를 내지 않아 생성 타입이 없다. 아래 스키마는 2026-10-13 hcms·hcms-staging 의 공개 응답
// (항목 전체)으로 확인한 모양이다. 앱이 읽지 않는 노출 대상 필드(target·target2·authority·include·exclude 등)는
// 요청 필터로만 쓰여 받지 않는다. Strapi 컴포넌트가 함께 싣는 `id` 도 같은 이유로 받지 않는다.

// 공지사항(고객센터)
export const ACADEMY_NOTICE_TYPE = ['NOTICE', 'CONTENT'] as const
export const AcademyNoticeTypeSchema = z.enum(ACADEMY_NOTICE_TYPE)
export const AcademyNoticeAttributesSchema = z.object({
  title: z.string(),
  content: z.string(),
  type: AcademyNoticeTypeSchema,
  publishedAt: _utcDatetime,
})
export type AcademyNotice = { id: number } & z.infer<typeof AcademyNoticeAttributesSchema>

// 자주 묻는 질문. 분류를 비워 둔 항목이 있어 null 이 온다.
export const FAQ_TYPE = [
  'FREE_TRIAL',
  'PAYMENT',
  'SERVICE',
  'DEVICE',
  'ETC',
  'PRINTING_SETTLEMENT',
  'KMM',
] as const
export const FaqTypeSchema = z.enum(FAQ_TYPE)
export const FaqAttributesSchema = z.object({
  type: FaqTypeSchema.nullable(),
  question: z.string(),
  content: z.string(),
  priority: z.number(),
})
export type Faq = { id: number } & z.infer<typeof FaqAttributesSchema>

const StrapiMediaSchema = z.object({
  attributes: z.object({ name: z.string(), url: z.string() }),
})
export type NoticeMedia = z.infer<typeof StrapiMediaSchema>['attributes']

const PopupLinkButtonSchema = z.object({ title: z.string(), link: z.string() })

// 알림판
export const NOTICE_BOARD_TYPE = [
  'UPDATE_PRODUCT',
  'UPDATE_CONTENTS',
  'GENERAL',
  'EVENT',
  'URGENT',
  'ETC',
] as const
export const NoticeBoardTypeSchema = z.enum(NOTICE_BOARD_TYPE)
export const NoticeBoardAttributesSchema = z.object({
  type: NoticeBoardTypeSchema,
  title: z.string(),
  content: z.string(),
  memo: z.string().nullable(),
  publishedAt: _utcDatetime,
  /** 상단 고정을 끝내는 날짜 */
  pinEnd: z.iso.date().nullable(),
  popupLinkButton: PopupLinkButtonSchema.nullable(),
  /** 줄바꿈으로 나열한 학원 코드. 비어 있으면 모든 학원에 보인다 */
  targetDCode: z.string().nullable(),
  media: z.object({ data: z.array(StrapiMediaSchema).nullable() }),
})
export type NoticeBoard = { id: number } & Omit<
  z.infer<typeof NoticeBoardAttributesSchema>,
  'media'
> & {
    media: NoticeMedia[]
  }

// 학생 앱 팝업 공지. 노출 대상(targetPlanTypes·targetUserTypes)은 요청 필터로만 쓰고, 작성·게시 시각은 앱이 읽지 않아
// 받지 않는다. 2026-10-16 기준 학생 앱이 읽던 필드를 근거로 적었다.
export const STUDENT_NOTICE_DO_NOT_SHOW_AGAIN_OPTION = [
  'NONE',
  'DO_NOT_SHOW_AGAIN',
  'DO_NOT_SHOW_AGAIN_FOR_THREE_DAYS',
] as const
export const StudentNoticePopupAttributesSchema = z.object({
  title: z.string(),
  content: z.string(),
  doNotShowAgainOption: z.enum(STUDENT_NOTICE_DO_NOT_SHOW_AGAIN_OPTION),
  priority: z.number(),
  popupLinkButton: PopupLinkButtonSchema.nullable(),
  imageUrl: z.string().nullable(),
})
export type StudentNoticePopup = { id: number } & z.infer<typeof StudentNoticePopupAttributesSchema>

/** 팝업 공지를 거르는 요금제·사용자 종류 */
export const STUDENT_NOTICE_TARGET_PLAN_TYPE = ['BASIC', 'PREMIUM', 'SOLO'] as const
export type StudentNoticeTargetPlanType = (typeof STUDENT_NOTICE_TARGET_PLAN_TYPE)[number]
export const STUDENT_NOTICE_TARGET_USER_TYPE = ['PARENT', 'STUDENT'] as const
export type StudentNoticeTargetUserType = (typeof STUDENT_NOTICE_TARGET_USER_TYPE)[number]

// 온보딩 팝업. name 은 CMS 작성자가 정하는 키라 닫힌 목록으로 좁히지 않는다.
export const OnboardingPopupAttributesSchema = z.object({
  name: z.string(),
  priority: z.number(),
  onboardings: z.array(
    z.object({
      id: z.number(),
      title: z.string(),
      content: z.string(),
      imageUrl: z.string(),
    }),
  ),
})
export type OnboardingPopup = { id: number } & z.infer<typeof OnboardingPopupAttributesSchema>

// 무료체험 툴팁 배너
export const BannerAttributesSchema = z.object({
  tooltipImageUrl: z.string(),
  tooltipTextColor: z.string(),
  longImageUrl: z.string(),
  longImagePrimaryColor: z.string(),
  longImageSecondaryColor: z.string(),
  redirectUrl: z.string().nullable(),
  isTimerTextVisible: z.boolean(),
  expiredAt: _utcDatetime,
})
export type Banner = { id: number } & z.infer<typeof BannerAttributesSchema>

// 업데이트 콘텐츠(로그인 직후 띄우는 Framer 페이지)
export const UpdatedContentAttributesSchema = z.object({
  /** 줄바꿈으로 나열한 학원 코드. 여기 든 학원은 나머지 노출 조건을 보지 않는다 */
  dcodes: z.string().nullable(),
  showStartDate: _utcDatetime,
  showEndDate: _utcDatetime,
  priority: z.number(),
  framerUrl: z.string(),
  maxViewsPerDevice: z.number().nullable(),
  academyAuthority: z.object({ BASIC: z.boolean(), PREMIUM: z.boolean(), SOLO: z.boolean() }),
  academyStatus: z.object({
    FREE_INPROGRESS: z.boolean(),
    PAY_SCHEDULED: z.boolean(),
    PAY_INPROGRESS: z.boolean(),
  }),
  // CREVERSE·VINE 도 함께 오지만 비어 있는(null) 항목이 있고 앱은 B2B·B2G 만 본다.
  academyType: z.object({ B2B: z.boolean(), B2G: z.boolean() }),
})
export type UpdatedContent = { id: number } & z.infer<typeof UpdatedContentAttributesSchema>

// 서버 점검 안내
export const ServerMaintenanceAttributesSchema = z.object({
  content: z.string(),
  /** 화면에 그대로 보이는 점검 기간 문구 */
  period: z.string(),
  startDate: _utcDatetime,
  endDate: _utcDatetime,
})
export type ServerMaintenance = z.infer<typeof ServerMaintenanceAttributesSchema>

// 이용 후기
export const ReviewAttributesSchema = z.object({
  title: z.string(),
  content: z.string(),
  priority: z.number(),
  thumbnail: z.object({ data: StrapiMediaSchema }),
})
export type Review = { id: number } & Omit<z.infer<typeof ReviewAttributesSchema>, 'thumbnail'> & {
    thumbnail: NoticeMedia
  }

// 아래 둘은 2026-10-14 hcms·hcms-staging 의 공개 응답(/vods 항목 전체·/mathflat-home-buttons)으로 확인했다.

// 영상(VOD)·칼럼. 미디어는 올리지 않은 항목이 있어 data 가 null 로 온다. 영상은 저자·요약·본문이, 칼럼은 영상
// 주소가 비어(null) 온다.
export const VOD_COLUMN_TYPE = ['VOD', 'COLUMN'] as const
export const VodColumnTypeSchema = z.enum(VOD_COLUMN_TYPE)
export type VodColumnType = z.infer<typeof VodColumnTypeSchema>

const _OptionalMediaSchema = z.object({ data: StrapiMediaSchema.nullable() })

export const VodColumnAttributesSchema = z.object({
  type: VodColumnTypeSchema,
  title: z.string(),
  priority: z.number(),
  is_member_only: z.boolean(),
  summary: z.string().nullable(),
  /** 칼럼 본문 HTML */
  contents: z.string().nullable(),
  author_name: z.string().nullable(),
  author_info: z.string().nullable(),
  youtube_video_id: z.string().nullable(),
  video_url: z.string().nullable(),
  thumbnail: _OptionalMediaSchema,
  /** 회원 전용 콘텐츠를 비회원에게 보일 때 쓰는 가린 썸네일 */
  thumbnail_x: _OptionalMediaSchema,
  author_thumbnail: _OptionalMediaSchema,
  pdf: _OptionalMediaSchema,
})
type _VodColumnMediaKey = 'thumbnail' | 'thumbnail_x' | 'author_thumbnail' | 'pdf'
export type VodColumn = { id: number } & Omit<
  z.infer<typeof VodColumnAttributesSchema>,
  _VodColumnMediaKey
> &
  Record<_VodColumnMediaKey, NoticeMedia | null>

// 로그인 뒤 홈 버튼이 데려갈 곳. 브랜드마다 기간을 두고 걸며, 학원 코드별로 다른 주소를 걸 수 있다.
// 학원 코드(Dcode)는 Strapi 리치 텍스트 블록이라 문단 안의 텍스트 노드에 코드가 들어 있다.
const _RichTextBlockSchema = z.object({
  type: z.string(),
  children: z.array(z.object({ type: z.string(), text: z.string().optional() })),
})

export const HomeButtonLinkSchema = z.object({
  url: z.string(),
  startDatetime: _utcDatetime,
  endDatetime: _utcDatetime,
  customUrls: z.array(
    z.object({
      customUrl: z.string(),
      Dcode: z.array(_RichTextBlockSchema),
    }),
  ),
})
export type HomeButtonLink = z.infer<typeof HomeButtonLinkSchema>

export const HomeButtonLinksAttributesSchema = z.object({
  homeButtonLinkMathflat: HomeButtonLinkSchema.nullable(),
  homeButtonLinkSchoolflat: HomeButtonLinkSchema.nullable(),
})
export type HomeButtonLinks = { id: number } & z.infer<typeof HomeButtonLinksAttributesSchema>
